import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Logo } from "./Logo";
import { lockScroll } from "../utils/scrollLock";

/**
 * Branded boot sequence. Covers the app the moment it mounts so the first thing on screen is
 * the SMZ mark. The counter and progress bar are written straight to the DOM, so the whole
 * sequence costs zero re-renders.
 */
export function BootSplash() {
  const reduceMotion = useReducedMotion();
  const [done, setDone] = useState(false);
  const counterRef = useRef<HTMLSpanElement>(null);
  const barRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (done) return;
    const release = lockScroll();
    const duration = reduceMotion ? 400 : 1400;
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const linear = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - linear, 3);
      if (counterRef.current) counterRef.current.textContent = String(Math.round(eased * 100)).padStart(3, "0");
      if (barRef.current) barRef.current.style.transform = `scaleX(${eased.toFixed(4)})`;
      if (linear < 1) {
        frame = window.requestAnimationFrame(tick);
        return;
      }
      setDone(true);
    };
    frame = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(frame);
      release();
    };
  }, [done, reduceMotion]);
  return <AnimatePresence data-id="element-10" data-mp="3orxtlvd1q41o">
      {!done && <motion.div key="boot" role="status" aria-label="Loading" className="fixed inset-0 z-[200] grid place-items-center overflow-hidden bg-[#050808]" initial={{
      opacity: 1
    }} exit={reduceMotion ? {
      opacity: 0
    } : {
      y: "-101%"
    }} transition={{
      duration: reduceMotion ? 0.3 : 0.9,
      ease: [0.76, 0, 0.24, 1]
    }} data-id="element-11" data-mp="3oqwjbt3utll0">
          <div aria-hidden="true" className="absolute inset-0 film-grain opacity-[0.18]" data-id="element-12" data-mp="cbc50qlkwtpx" />
          <div aria-hidden="true" className="absolute inset-0 vignette" data-id="element-13" data-mp="je93a8edjx0i" />

          <div aria-hidden="true" className="pointer-events-none absolute inset-5 hidden md:block" data-id="element-14" data-mp="octwvoz4fifz">
            <span className="absolute left-0 top-0 h-7 w-px bg-white/20" data-id="element-15" data-mp="tbx2pbmxdsp0" />
            <span className="absolute left-0 top-0 h-px w-7 bg-white/20" data-id="element-16" data-mp="t9u0zuceub4g" />
            <span className="absolute right-0 top-0 h-7 w-px bg-white/20" data-id="element-17" data-mp="y3f05ppqpb58" />
            <span className="absolute right-0 top-0 h-px w-7 bg-white/20" data-id="element-18" data-mp="12509j132f7ao" />
            <span className="absolute bottom-0 left-0 h-7 w-px bg-white/20" data-id="element-19" data-mp="1731szn4el8mo" />
            <span className="absolute bottom-0 left-0 h-px w-7 bg-white/20" data-id="element-20" data-mp="1754ub3b0ao6p" />
            <span className="absolute bottom-0 right-0 h-7 w-px bg-white/20" data-id="element-21" data-mp="1c36c7kwp4hgl" />
            <span className="absolute bottom-0 right-0 h-px w-7 bg-white/20" data-id="element-22" data-mp="1h06inwxgjm88" />
          </div>

          <div className="relative w-[min(80vw,268px)]" data-id="element-23" data-mp="3dut6bvbvwsha">
            <div className="relative overflow-hidden" data-id="element-24" data-mp="3isuqkjl1qxuc">
              <motion.div className="flex justify-center" initial={reduceMotion ? {
            opacity: 0
          } : {
            y: "48%",
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1
          }} transition={{
            duration: 0.95,
            ease: [0.16, 1, 0.3, 1]
          }} data-id="element-25" data-mp="3ite7py2vhi7b">
                <Logo className="h-14 w-[124px]" data-id="element-26" data-mp="1r67tgh15yoaf" />
              </motion.div>

              {!reduceMotion && <motion.span aria-hidden="true" className="absolute inset-y-0 left-0 w-20 bg-[linear-gradient(90deg,transparent,rgba(84,246,198,0.45),transparent)]" initial={{
            x: "-160%"
          }} animate={{
            x: "420%"
          }} transition={{
            duration: 1.2,
            delay: 0.3,
            ease: "easeInOut"
          }} data-id="element-27" data-mp="1m86arx7ngqzh" />}
            </div>

            <motion.div className="mt-9 flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-white/45" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.35,
          duration: 0.5
        }} data-id="element-28" data-mp="24z76mcdh46et">
              <span data-id="element-29" data-mp="2hcizerbzgcpv">Loading</span>
              <span ref={counterRef} className="text-white/75" data-id="element-30" data-mp="2m5ltr1ol3q74">000</span>
            </motion.div>

            <span aria-hidden="true" className="mt-3 block h-px w-full overflow-hidden bg-white/15" data-id="element-31" data-mp="28e7aquuofp02">
              <span ref={barRef} className="block h-px w-full origin-left bg-[#54f6c6]" style={{
            transform: "scaleX(0)"
          }} data-id="element-32" data-mp="2dbz4zvisnn7f" />
            </span>

            <motion.p className="mt-5 text-center font-mono text-[11px] uppercase tracking-[0.22em] text-white/35" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.55,
          duration: 0.6
        }} data-id="element-33" data-mp="3134hc0pizxcz">
              Syed Muhammad Zeeshan
            </motion.p>
          </div>
        </motion.div>}
    </AnimatePresence>;
}